import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { ItemCategoryRecord } from '@/types/item';

interface ItemCategoryInput {
  name: string;
  description?: string;
  color?: string;
  sort_order?: number;
}

export const useItemCategories = () => {
  return useQuery({
    queryKey: ['item-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('item_categories')
        .select('id, name, description, color, sort_order')
        .eq('is_active', true)
        .order('sort_order')
        .limit(50);
      
      if (error) throw error;
      return data as ItemCategoryRecord[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - prevent perceived reloads
    refetchOnMount: true, // Only refetch if stale
  });
};

export const useCreateItemCategory = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (input: ItemCategoryInput) => {
      const { data, error } = await supabase
        .from('item_categories')
        .insert([{
          name: input.name,
          description: input.description,
          color: input.color || '#64748b',
          sort_order: input.sort_order || 0
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item-categories'] });
      toast({
        title: "Thành công",
        description: "Đã tạo danh mục mới."
      });
    },
    onError: (error) => {
      console.error('Error creating item category:', error);
      toast({
        title: "Lỗi tạo danh mục",
        description: "Không thể tạo danh mục mới. Vui lòng thử lại.",
        variant: "destructive"
      });
    }
  });
};

export const useUpdateItemCategory = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<ItemCategoryRecord> & { id: string }) => {
      const { data, error } = await supabase
        .from('item_categories')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item-categories'] });
      toast({
        title: "Thành công",
        description: "Đã cập nhật danh mục."
      });
    },
    onError: (error) => {
      console.error('Error updating item category:', error);
      toast({
        title: "Lỗi cập nhật",
        description: "Không thể cập nhật danh mục. Vui lòng thử lại.",
        variant: "destructive"
      });
    }
  });
};

export const useDeleteItemCategory = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (id: string) => {
      // First get the category name from the id
      const { data: category, error: categoryError } = await supabase
        .from('item_categories')
        .select('name')
        .eq('id', id)
        .single();
      
      if (categoryError) throw categoryError;
      
      // Check if category is being used by checking against category name
      const { data: itemsUsingCategory, error: checkError } = await supabase
        .from('items')
        .select('id')
        .eq('category', category.name)
        .limit(1);
      
      if (checkError) throw checkError;
      
      if (itemsUsingCategory && itemsUsingCategory.length > 0) {
        throw new Error('Không thể xóa danh mục đang được sử dụng');
      }
      
      const { error } = await supabase
        .from('item_categories')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item-categories'] });
      toast({
        title: "Thành công",
        description: "Đã xóa danh mục."
      });
    },
    onError: (error) => {
      console.error('Error deleting item category:', error);
      toast({
        title: "Lỗi xóa danh mục",
        description: error.message || "Không thể xóa danh mục. Vui lòng thử lại.",
        variant: "destructive"
      });
    }
  });
};